import React, { useState, useRef } from "react";

const LAYOUTS = ["free", "box", "vertical", "horizontal"];
const SHAPES = ["square", "circle", "triangle"];

export default function WebDevSimulator() {
    const [layout, setLayout] = useState("free");
    const [bgColor, setBgColor] = useState("#ffffff");
    const [elements, setElements] = useState([]);
    const [texts, setTexts] = useState([]);
    const [hovering, setHovering] = useState(false); // for showing controls

    const containerRef = useRef();

    // Add shape element
    function addShape() {
        setElements(prev => [
            ...prev,
            { id: Date.now(), type: SHAPES[0], color: "#cccccc", x: 0, y: 0 }
        ]);
    }

    // Add text element
    function addText() {
        setTexts(prev => [
            ...prev,
            { id: Date.now(), content: "New text", color: "#000000", fontSize: 16, x: 0, y: 0 }
        ]);
    }

    function updateElement(id, key, value) {
        setElements(prev =>
            prev.map(el => (el.id === id ? { ...el, [key]: value } : el))
        );
    }

    function updateText(id, key, value) {
        setTexts(prev =>
            prev.map(t => (t.id === id ? { ...t, [key]: value } : t))
        );
    }

    function removeElement(id) {
        setElements(prev => prev.filter(el => el.id !== id));
    }
    function removeText(id) {
        setTexts(prev => prev.filter(t => t.id !== id));
    }

    const containerStyle = {
        display:
            layout === "vertical"
                ? "flex"
                : layout === "horizontal"
                    ? "flex"
                    : layout === "box"
                        ? "grid"
                        : "relative",
        flexDirection: layout === "vertical" ? "column" : "row",
        gridTemplateColumns:
            layout === "box" ? "repeat(auto-fit, minmax(100px, 1fr))" : undefined,
        position: "relative",
        gap: "10px",
        padding: "10px",
        backgroundColor: bgColor,
        minHeight: "300px",
        border: "2px solid black",
        marginTop: "20px",
        overflow: "hidden",
    };

    // CSS shape renderer
    function renderShape(shape, color) {
        switch (shape) {
            case "square":
                return <div style={{ width: "80px", height: "80px", backgroundColor: color }} />;
            case "circle":
                return <div style={{ width: "80px", height: "80px", backgroundColor: color, borderRadius: "50%" }} />;
            case "triangle":
                return (
                    <div
                        style={{
                            width: 0,
                            height: 0,
                            borderLeft: "40px solid transparent",
                            borderRight: "40px solid transparent",
                            borderBottom: `80px solid ${color}`,
                        }}
                    />
                );
            default:
                return null;
        }
    }

    // Dragging logic
    function handleDragStart(e, item, type) {
        const startX = e.clientX;
        const startY = e.clientY;
        const startLeft = item.x || 0;
        const startTop = item.y || 0;

        function onMouseMove(ev) {
            const dx = ev.clientX - startX;
            const dy = ev.clientY - startY;

            if (type === "shape") {
                updateElement(item.id, "x", startLeft + dx);
                updateElement(item.id, "y", startTop + dy);
            } else {
                updateText(item.id, "x", startLeft + dx);
                updateText(item.id, "y", startTop + dy);
            }
        }

        function onMouseUp() {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        }

        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
    }

    return (
        <div style={{ maxWidth: "900px", margin: "0 auto", textAlign: "center" }}>
            <h1>🖥️ Web Developer Simulator</h1>

            {/* Layout Selector */}
            <div style={{ margin: "10px 0" }}>
                <label>Layout: </label>
                <select value={layout} onChange={e => setLayout(e.target.value)}>
                    {LAYOUTS.map(l => (
                        <option key={l} value={l}>
                            {l}
                        </option>
                    ))}
                </select>
            </div>

            {/* Background Color */}
            <div style={{ margin: "10px 0" }}>
                <label>Background Color: </label>
                <input
                    type="color"
                    value={bgColor}
                    onChange={e => setBgColor(e.target.value)}
                />
            </div>

            {/* Add Buttons */}
            <div style={{ margin: "10px 0" }}>
                <button
                    onClick={addShape}
                    style={{
                        padding: "8px 16px",
                        marginRight: "10px",
                        backgroundColor: "#6f42c1",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                    }}
                >
                    Add Shape
                </button>
                <button
                    onClick={addText}
                    style={{
                        padding: "8px 16px",
                        backgroundColor: "#20c997",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                    }}
                >
                    Add Text
                </button>
            </div>

            {/* Container */}
            <div
                ref={containerRef}
                style={containerStyle}
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
            >
                {/* Shapes */}
                {elements.map(el => (
                    <div
                        key={el.id}
                        onMouseDown={layout === "free" ? e => handleDragStart(e, el, "shape") : undefined}
                        style={{
                            position: layout === "free" ? "absolute" : undefined,
                            left: layout === "free" ? el.x || 0 : undefined,
                            top: layout === "free" ? el.y || 0 : undefined,
                            cursor: layout === "free" ? "grab" : "pointer",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        {renderShape(el.type, el.color)}

                        {/* Controls */}
                        {hovering && (
                            <div style={{ marginTop: "5px", fontSize: "12px", display: "flex", justifyContent: "center" }}>
                                <select
                                    value={el.type}
                                    onChange={e => updateElement(el.id, "type", e.target.value)}
                                    style={{ fontSize: "12px" }}
                                >
                                    {SHAPES.map(s => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    type="color"
                                    value={el.color}
                                    onChange={e => updateElement(el.id, "color", e.target.value)}
                                    style={{ marginLeft: "5px", width: "20px", height: "20px", padding: 0, border: "none" }}
                                />
                                <button
                                    onClick={() => removeElement(el.id)}
                                    style={{
                                        marginLeft: "5px",
                                        fontSize: "10px",
                                        padding: "2px 4px",
                                        lineHeight: 1,
                                    }}
                                >
                                    ❌
                                </button>
                            </div>
                        )}
                    </div>
                ))}

                {/* Texts */}
                {texts.map(t => (
                    <div
                        key={t.id}
                        onMouseDown={layout === "free" ? e => handleDragStart(e, t, "text") : undefined}
                        style={{
                            position: layout === "free" ? "absolute" : undefined,
                            left: layout === "free" ? t.x || 0 : undefined,
                            top: layout === "free" ? t.y || 0 : undefined,
                            border: "1px dashed gray",
                            padding: "5px",
                            cursor: layout === "free" ? "grab" : "pointer",
                        }}
                    >
                        <input
                            type="text"
                            value={t.content}
                            onChange={e => updateText(t.id, "content", e.target.value)}
                            style={{ fontSize: t.fontSize, color: t.color }}
                        />
                        {hovering && (
                            <div style={{ marginTop: "5px", fontSize: "12px", display: "flex", justifyContent: "center" }}>
                                <input
                                    type="color"
                                    value={t.color}
                                    onChange={e => updateText(t.id, "color", e.target.value)}
                                    style={{ marginRight: "5px", width: "20px", height: "20px", padding: 0, border: "none" }}
                                />
                                <input
                                    type="number"
                                    value={t.fontSize}
                                    min={8}
                                    max={72}
                                    onChange={e => updateText(t.id, "fontSize", Number(e.target.value))}
                                    style={{ width: "50px" }}
                                />
                                <button
                                    onClick={() => removeText(t.id)}
                                    style={{ marginLeft: "5px", fontSize: "10px", padding: "2px 4px", lineHeight: 1 }}
                                >
                                    ❌
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
